// src/pages/RespondentDashboard.tsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useSelfAssessmentScores, type FnScore } from "@/hooks/useSelfAssessmentScores";
import { BrandedLogo } from "@/components/ui/BrandedLogo";
import csvProLogo from "@/assets/csv-pro-logo-new.png";
import { getLogoPublicUrl } from "@/lib/branding";
import { PREVIEW_ALLOWLIST } from "@/config/featureFlags";
import MfaSecurityCta from "@/components/auth/MfaSecurityCta";
import MfaStatusBanner from "@/components/auth/MfaStatusBanner";
import { getQuestionnaireDisplayTitle } from "@/lib/questionnaireDisplayTitle";
import { QaToolkitPanel } from "@/components/qa/QaToolkitPanel";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import {
  LogOut,
  Shield,
  Building2,
  Mail,
  ChevronRight,
  Clock,
  FileText,
  RotateCcw,
} from "lucide-react";

// -----------------
// Helper: fetch active respondent assessment via RPC
// -----------------
export async function fetchActiveRespondentAssessment() {
  const { data, error } = await supabase.rpc("get_my_active_assessment");

  

  if (error) throw error;

  const row = data?.[0];
  if (!row) return undefined;

  return {
    id: row.id as string,
    status: row.status as string,
    created_at: row.created_at as string,
    questionnaire_id: row.questionnaire_id as string,
    org_id: row.org_id as string,
    org_name: row.org_name ?? "",
    first_review_completed_at: (row as any).first_review_completed_at ?? null,
    started_at: (row as any).started_at ?? null,
    action_plan_published: !!(row as any).action_plan_published,
    action_plan_published_at: (row as any).action_plan_published_at ?? null,
  };
}

// -----------------
// Helpers
// -----------------
function getQuestionnaireName(questionnaireId: string): string {
  const names: Record<string, string> = {
    telecom_self_v1: "Telecom Security Self-Assessment",
    telecom_v1: "Telecom Full Security Assessment",
    telecom_v2: "Telecom CSF 2.0 Assessment",
    telecom_ai_v1: "Telecom AI-Assisted Assessment",
    nist_csf_v1: "NIST CSF Assessment",
    hrv_self_v1: "Cybersecurity Risk Exposure Assessment",
    ai_llm_self_v1: "AI & LLM Security Readiness Assessment",
    telecom_pre_investment_triage_v1: "Telecom Pre-Investment Cyber Risk Triage",
  };
  return names[questionnaireId] || "Cybersecurity Readiness Assessment";
}

function getProgressMessage(
  progress: number,
  answered: number,
  total: number
): string {
  if (progress >= 100) return `Completed (${answered}/${total} questions)`;
  if (total > 0) return `${answered} of ${total} questions answered`;
  return "Let's begin!";
}

/** Map assessment DB status + started_at + answered count to display status */
function getSelfAssessmentDisplayStatus(
  dbStatus: string,
  startedAt: string | null,
  answeredQuestions?: number,
  totalQuestions?: number,
): "Not Started" | "In Progress" | "Completed" | "Submitted" {
  if (dbStatus === "Submitted" || dbStatus === "In Review" || dbStatus === "Closed") {
    return "Submitted";
  }
  // Draft with all questions answered
  if (totalQuestions && totalQuestions > 0 && answeredQuestions === totalQuestions) {
    return "Completed";
  }
  // Draft with started_at or any responses
  if (startedAt || (answeredQuestions && answeredQuestions > 0)) return "In Progress";
  return "Not Started";
}

function getStatusColor(status: string): string {
  switch (status) {
    case "Submitted": return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
    case "Completed": return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300";
    case "In Progress": return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300";
    default: return "bg-muted text-muted-foreground";
  }
}

function getAssessmentHelperText(questionnaireId: string): { progress: string; description: string } {
  switch (questionnaireId) {
    case "ai_llm_self_v1":
      return {
        progress: "Progress reflects completion of the AI & LLM security readiness evaluation. You may pause and return at any time.",
        description: "The assessment evaluates your organization's AI and Large Language Model security posture across key domains including AI governance, data protection, model security, prompt injection controls, and responsible AI deployment.",
      };
    case "hrv_self_v1":
      return {
        progress: "Progress reflects completion of the high-risk vendor exposure assessment. You may pause and return at any time.",
        description: "The assessment evaluates cybersecurity risk exposure across critical vendor management domains to determine technical assessment requirements.",
      };
    case "telecom_pre_investment_triage_v1":
      return {
        progress: "Progress reflects completion of the Telecom Pre-Investment Cyber Risk Triage. You may pause and return at any time.",
        description: "Rapidly assesses a telecom target's inherent cybersecurity exposure, investment risk, red flags, and areas requiring deeper diligence before or during the investment process. This is an inherent-exposure triage, not a control-maturity assessment.",
      };
    default:
      return {
        progress: "Progress reflects completion of the cybersecurity readiness evaluation. You may pause and return at any time.",
        description: "The assessment evaluates cybersecurity readiness across six domains: Governance, Identify, Protect, Detect, Respond, and Recover.",
      };
  }
}

// Mini NIST function bar for dashboard
function FunctionScoreMini({ scores }: { scores: FnScore[] }) {
  if (!scores || scores.length === 0) return null;
  return (
    <div className="grid grid-cols-6 gap-1.5 mt-3">
      {scores.map((s) => {
        const pct = Math.round(s.score);
        const color = pct >= 75 ? "bg-green-500" : pct >= 25 ? "bg-amber-500" : pct === 0 ? "bg-red-200 border border-red-400" : "bg-red-400";
        return (
          <div key={s.function} className="text-center">
            <div className="text-[10px] font-medium text-muted-foreground mb-0.5">{s.function}</div>
            <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
              <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.max(pct, pct === 0 ? 100 : 0)}%` }} />
            </div>
            <div className="text-[10px] tabular-nums text-muted-foreground mt-0.5">{pct}%</div>
          </div>
        );
      })}
    </div>
  );
}

// -----------------
// Component
// -----------------
export default function RespondentDashboard() {
  const navigate = useNavigate();
  const [portfolioLogoUrl, setPortfolioLogoUrl] = useState<string | null>(null);
  const [logoLoading, setLogoLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [assessment, setAssessment] = useState<null | {
    id: string;
    status: string;
    created_at: string;
    questionnaire_id: string;
    org_id: string;
    org_name: string;
    first_review_completed_at: string | null;
    started_at: string | null;
    action_plan_published?: boolean;
    action_plan_published_at?: string | null;
  }>(null);
  const [error, setError] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string>("");
  const [realProgress, setRealProgress] = useState<number>(0);
  const [totalQuestions, setTotalQuestions] = useState<number>(0);
  const [answeredQuestions, setAnsweredQuestions] = useState<number>(0);
  const [hrvSummary, setHrvSummary] = useState<{
    technical_assessment_recommended: boolean;
    technical_assessment_type: 'full' | 'targeted' | null;
    domain_scores?: Array<{ domain: string; label: string; score: number; exposure_label: string }>;
  } | null>(null);
  const [actionPlanPublished, setActionPlanPublished] = useState<boolean>(false);

  // Fetch only the portfolio company's own logo (no client/sub-org fallback)
  useEffect(() => {
    if (!assessment?.org_id) {
      setPortfolioLogoUrl(null);
      setLogoLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.rpc(
          "respondent_get_portfolio_logo" as any,
          { p_org_id: assessment.org_id }
        );
        if (!cancelled) {
          const row = Array.isArray(data) ? data[0] : data;
          const raw = row?.logo_url;
          setPortfolioLogoUrl(raw ? getLogoPublicUrl(raw) : null);
          setLogoLoading(false);
        }
      } catch {
        if (!cancelled) {
          setPortfolioLogoUrl(null);
          setLogoLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [assessment?.org_id]);

  const isNistSelfAssessment = assessment?.questionnaire_id === "telecom_self_v1" || assessment?.questionnaire_id === "ai_llm_self_v1";
  const isSelfAssessment = isNistSelfAssessment;
  const isHrv = assessment?.questionnaire_id === "hrv_self_v1";
  const isTriage = assessment?.questionnaire_id === "telecom_pre_investment_triage_v1";
  const { byFn, overall } = useSelfAssessmentScores(
    isNistSelfAssessment && assessment ? assessment.id : null
  );

  // -----------------
  // Effect: load assessment + progress
  // -----------------
  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) {
          navigate("/login");
          return;
        }

        setUserEmail(session.user.email || "");
        setLoading(true);

        const row = await fetchActiveRespondentAssessment();
        if (!active) return;

        if (!row) {
          setAssessment(null);
          setError(null);
        } else {
          setAssessment(row);
          setError(null);

          const { data: progressRpc, error: progressErr } = await (supabase.rpc as any)(
            'respondent_get_progress',
            { p_assessment_id: row.id }
          );
          if (progressErr) {
            console.warn('[RespondentDashboard] respondent_get_progress error:', progressErr);
          }
          const progressRow = Array.isArray(progressRpc) ? progressRpc[0] : progressRpc;
          const total = progressRow?.total_questions ?? 0;
          const answered = progressRow?.answered_questions ?? 0;
          setTotalQuestions(total);
          setAnsweredQuestions(answered);

          const progress =
            total > 0 ? Math.round((answered / total) * 100) : 0;
          setRealProgress(progress);

          // Fetch HRV summary for recommendation data
          if (row.questionnaire_id === 'hrv_self_v1' && answered > 0) {
            const { data: hrvData, error: hrvErr } = await (supabase.rpc as any)(
              'respondent_get_hrv_assessment_summary',
              { p_assessment_id: row.id }
            );
            if (!hrvErr && hrvData) {
              setHrvSummary({
                technical_assessment_recommended: hrvData.technical_assessment_recommended ?? false,
                technical_assessment_type: hrvData.technical_assessment_type ?? null,
                domain_scores: hrvData.domain_scores ?? [],
              });
              if (import.meta.env.DEV) {
                console.log('[RespondentDashboard] HRV summary:', hrvData);
              }
            }
          }

          // Phase 2: detect published Action Plan for telecom_v2 (gate UI button)
          // Sourced directly from get_my_active_assessment RPC payload to bypass
          // RLS issues on direct assessment_action_plans table reads.
          if ((row.questionnaire_id as string) === 'telecom_v2') {
            setActionPlanPublished(!!row.action_plan_published);
          } else {
            setActionPlanPublished(false);
          }
          console.log('[RespondentDashboard] CAP payload', {
            assessment_id: row.id,
            questionnaire_id: row.questionnaire_id,
            raw_status: row.status,
            action_plan_published: !!row.action_plan_published,
            action_plan_published_at: row.action_plan_published_at ?? null,
          });
        }
      } catch (e: any) {
        if (!active) return;
        console.error("[RespondentDashboard] fetch error:", e);

        if (e?.message?.includes("Refresh Token")) {
          await supabase.auth.signOut();
          navigate("/login");
          return;
        }

        setError(e?.message || "Unable to load your assessment");
        setAssessment(null);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [navigate]);

  // -----------------
  // Derived state for telecom_self_v1
  // -----------------
  const overallScore = overall ? Math.round(overall.overallScore) : null;

  // Use scores hook progress if available (more accurate for self-assessment)
  const effectiveProgress = isSelfAssessment && overall
    ? (overall.totalQuestions > 0 ? Math.round((overall.answeredQuestions / overall.totalQuestions) * 100) : 0)
    : realProgress;
  const effectiveAnswered = isSelfAssessment && overall ? overall.answeredQuestions : answeredQuestions;
  const effectiveTotal = isSelfAssessment && overall ? overall.totalQuestions : totalQuestions;

  const displayStatus = assessment
    ? (isNistSelfAssessment || isHrv || isTriage)
      ? getSelfAssessmentDisplayStatus(assessment.status, assessment.started_at, effectiveAnswered, effectiveTotal)
      : assessment.status
    : "Draft";

  const isSubmitted = displayStatus === "Submitted";
  const isAllowlisted = PREVIEW_ALLOWLIST.includes(userEmail.toLowerCase());

  // -----------------
  // UI helpers
  // -----------------
  const getButtonConfig = (): { text: string; action: () => void }[] => {
    if (!assessment) return [{ text: "Start Assessment", action: () => {} }];

    // Determine if assessment has been started (by timestamp or by having responses)
    const hasStarted = assessment.started_at !== null;
    const hasResponses = answeredQuestions > 0;
    const shouldContinue = hasStarted || hasResponses;
    const progressAction = {
      text: shouldContinue ? "Continue Assessment" : "Start Assessment",
      action: () => navigate(`/wizard/${assessment.id}`),
    };

    // HRV self-assessment buttons
    if (isHrv) {
      if (isAllowlisted) {
        return [
          progressAction,
          { text: "Preview Report", action: () => navigate(`/reports/hrv/${assessment.id}`) },
        ];
      }
      if (isSubmitted) {
        return [
          { text: "View Report", action: () => navigate(`/reports/hrv/${assessment.id}`) },
          { text: "View Responses (Read-Only)", action: () => navigate(`/wizard/${assessment.id}`) },
        ];
      }
      return [progressAction];
    }

    // Pre-Investment Triage buttons
    if (isTriage) {
      if (isAllowlisted) {
        return [
          progressAction,
          { text: "Preview Triage Report", action: () => navigate(`/reports/triage/${assessment.id}`) },
        ];
      }
      if (isSubmitted) {
        return [
          { text: "View Triage Report", action: () => navigate(`/reports/triage/${assessment.id}`) },
          { text: "View Responses (Read-Only)", action: () => navigate(`/wizard/${assessment.id}`) },
        ];
      }
      return [progressAction];
    }

    // Telecom / AI self-assessment buttons
    if (isSelfAssessment) {
      if (isAllowlisted) {
        return [
          progressAction,
          { text: "Preview Report", action: () => navigate(`/reports/self/${assessment.id}`) },
        ];
      }
      if (isSubmitted) {
        return [
          { text: "View Report", action: () => navigate(`/reports/self/${assessment.id}`) },
          { text: "View Responses (Read-Only)", action: () => navigate(`/wizard/${assessment.id}`) },
        ];
      }
      return [progressAction];
    }

    // Non-self-assessment (existing logic)
    if (assessment.status === "Submitted" || assessment.status === "In Review")
      return [{ text: "View Assessment Submission", action: () => navigate(`/wizard/${assessment.id}`) }];
    if (assessment.status === "Closed")
      return [{ text: "View Results", action: () => navigate(`/wizard/${assessment.id}`) }];
    if (shouldContinue)
      return [{ text: "Continue Assessment", action: () => navigate(`/wizard/${assessment.id}`) }];
    return [{ text: "Start Assessment", action: () => navigate(`/wizard/${assessment.id}`) }];
  };

  const handleSignOut = async () => {
    try {
      localStorage.removeItem("supabase.auth.token");
      localStorage.removeItem("cyber-assess-state");
      await supabase.auth.signOut();
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
      navigate("/login");
    }
  };

  // QA Reset functionality
  const { toast } = useToast();
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetting, setResetting] = useState(false);

  const handleResetAssessment = async () => {
    if (!assessment) return;
    setResetting(true);
    try {
      const { error } = await (supabase.rpc as any)('respondent_reset_responses', {
        p_assessment_id: assessment.id,
      });
      if (error) throw error;
      toast({ title: "Assessment Reset", description: "All answers have been cleared. You can start fresh." });
      setShowResetDialog(false);
      // Reload the page to reflect changes
      window.location.reload();
    } catch (err: any) {
      console.error("[ResetAssessment] error:", err);
      toast({ variant: "destructive", title: "Reset Failed", description: err?.message ?? "Could not reset assessment." });
    } finally {
      setResetting(false);
    }
  };

  // -----------------
  // Render: loading / error / no assessment
  // -----------------
  if (loading)
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30 p-6 md:p-10 flex items-center justify-center">
        <div className="text-center space-y-3">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto" />
          <p className="text-sm text-muted-foreground">
            Loading your assessment…
          </p>
        </div>
      </div>
    );

  if (error)
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30 p-6 md:p-10">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight mb-4">
            Respondent Dashboard
          </h1>
          <Card className="border-destructive/20 bg-destructive/5">
            <CardContent className="p-6">
              <p className="text-sm text-destructive">{error}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );

  if (!assessment)
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30 p-6 md:p-10">
        <div className="max-w-2xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
                Welcome
              </h1>
              <p className="text-muted-foreground mt-1">Respondent Dashboard</p>
            </div>
            <div className="flex items-center gap-2">
              <MfaSecurityCta />
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSignOut}
                className="text-muted-foreground hover:text-foreground"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
          <MfaStatusBanner />
          <Card className="border-dashed">
            <CardContent className="p-8 text-center">
              <Shield className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
              <p className="text-muted-foreground">
                No active assessment found for your account.
                <br />
                If you were just assigned, refresh in a moment—or contact your
                admin.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );

  const buttons = getButtonConfig();

  // -----------------
  // Render: main dashboard
  // -----------------
  return (
    <div className="min-h-screen bg-background p-6 md:p-10">
      {/* Header */}
      <div className="max-w-3xl mx-auto mb-8">
        <div className="flex justify-between items-center">
          <img src={csvProLogo} alt="CSV Pro" className="h-24 w-auto" />
          <div className="flex items-center gap-2">
            <MfaSecurityCta />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSignOut}
              className="text-muted-foreground hover:text-foreground"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
        <div className="mt-4"><MfaStatusBanner /></div>
      </div>

      {/* QA Tools (only renders for QA-enabled users assigned to this assessment) */}
      <div className="max-w-3xl mx-auto mb-4">
        <QaToolkitPanel assessmentId={assessment.id} onMutated={() => window.location.reload()} />
      </div>

      {/* Main Assessment Card */}
      <Card className="max-w-3xl mx-auto p-6 shadow-sm border border-border">
        {/* Header / Hero */}
        <div className="flex items-center gap-3 mb-4">
          <BrandedLogo
            src={portfolioLogoUrl}
            alt="Organization"
            className="h-10 w-auto shrink-0"
            loading={logoLoading}
            fallback={<Building2 className="h-10 w-10 text-muted-foreground shrink-0" />}
          />
          <div className="flex flex-col gap-1">
            <h2 className="text-2xl font-semibold tracking-tight">
              {getQuestionnaireDisplayTitle(assessment.questionnaire_id)}
            </h2>
            <p className="text-sm text-muted-foreground">
              {isTriage
                ? "Rapidly assess cyber risk exposure and identify whether deeper cybersecurity diligence is required prior to investment."
                : "Assess your organization's current maturity and identify prioritized actions to strengthen your risk posture."}
            </p>
          </div>
        </div>

        {/* Two-column summary */}
        <div className="grid gap-6 md:grid-cols-[minmax(0,2fr)_minmax(0,1.5fr)] items-start mb-6">
          {/* Left: meta info */}
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <Building2 className="h-3 w-3 text-muted-foreground" />
              <span className="text-xs uppercase tracking-wide text-muted-foreground">
                Organization
              </span>
              <span className="font-medium">{assessment.org_name || "—"}</span>
            </div>

            <div className="flex items-center gap-2">
              <Mail className="h-3 w-3 text-muted-foreground" />
              <span className="text-xs uppercase tracking-wide text-muted-foreground">
                Assigned to
              </span>
              <span className="font-medium">{userEmail}</span>
            </div>

            <div className="flex items-center gap-2">
              <Clock className="h-3 w-3 text-muted-foreground" />
              <span className="text-xs uppercase tracking-wide text-muted-foreground">
                Status
              </span>
              {(() => {
                const inRemediation =
                  (assessment.questionnaire_id as string) === 'telecom_v2' &&
                  actionPlanPublished;
                const label = inRemediation ? 'In Remediation' : displayStatus;
                const cls = inRemediation
                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                  : getStatusColor(displayStatus);
                return (
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
                    {label}
                  </span>
                );
              })()}
            </div>

            {/* Overall Score — only show when submitted */}
            {isSelfAssessment && overallScore !== null && isSubmitted && (
              <div className="flex items-center gap-2">
                <Shield className="h-3 w-3 text-muted-foreground" />
                <span className="text-xs uppercase tracking-wide text-muted-foreground">
                  Overall Score
                </span>
                <span className="font-semibold text-base">{overallScore}%</span>
              </div>
            )}
          </div>

          {/* Right: progress summary */}
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">
                Progress
              </span>
              <span className="font-semibold">{effectiveProgress}%</span>
            </div>
            <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
              <div
                className="h-2 rounded-full bg-foreground transition-all"
                style={{ width: `${effectiveProgress}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {getProgressMessage(effectiveProgress, effectiveAnswered, effectiveTotal)}
            </p>
            <p className="text-xs text-muted-foreground">
              {getAssessmentHelperText(assessment.questionnaire_id).progress}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              {getAssessmentHelperText(assessment.questionnaire_id).description}
            </p>

            {/* Mini NIST function scores — only show when submitted */}
            {isSelfAssessment && byFn && byFn.length > 0 && isSubmitted && (
              <FunctionScoreMini scores={byFn} />
            )}

            {/* HRV Domain Risk Profile */}
            {isHrv && hrvSummary?.domain_scores && hrvSummary.domain_scores.length > 0 && (
              <div className="mt-3">
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Risk Profile</p>
                <div className="space-y-1.5">
                  {hrvSummary.domain_scores.map((d) => {
                    const dot = d.exposure_label === "High Exposure" ? "🔴" : d.exposure_label === "Moderate Exposure" ? "🟡" : "🟢";
                    const riskLabel = d.exposure_label === "High Exposure" ? "High Risk" : d.exposure_label === "Moderate Exposure" ? "Moderate Risk" : "Low Risk";
                    return (
                      <div key={d.domain} className="flex items-center justify-between text-xs">
                        <span className="flex items-center gap-1.5">
                          <span>{dot}</span>
                          <span className="font-medium">{d.label}</span>
                        </span>
                        <span className="text-muted-foreground">{riskLabel}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* HRV Technical Assessment Recommendation */}
        {isHrv && isSubmitted && hrvSummary?.technical_assessment_recommended && (
          <div className={`rounded-lg border p-4 mb-4 ${
            hrvSummary.technical_assessment_type === 'full'
              ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950/30'
              : 'border-yellow-300 bg-yellow-50 dark:border-yellow-700 dark:bg-yellow-950/30'
          }`}>
            <p className={`text-sm font-semibold mb-2 ${
              hrvSummary.technical_assessment_type === 'full'
                ? 'text-red-700 dark:text-red-300'
                : 'text-yellow-700 dark:text-yellow-300'
            }`}>
              {hrvSummary.technical_assessment_type === 'full'
                ? '🔴 Full Technical Assessment Recommended'
                : '🟡 Targeted Technical Review Recommended'}
            </p>
            <p className="text-xs text-muted-foreground">
              {hrvSummary.technical_assessment_type === 'full'
                ? 'Based on the HRV exposure profile, a full technical cybersecurity assessment is recommended.'
                : 'Based on the HRV exposure profile, a targeted technical review is recommended in higher-risk domains.'}
            </p>
          </div>
        )}

        {/* Footer CTA */}
        <div className="flex flex-col gap-3">
          {(() => {
            const inRemediation =
              (assessment.questionnaire_id as string) === 'telecom_v2' &&
              actionPlanPublished;
            return (
              <>
                {inRemediation && (
                  <div className="border-t border-border pt-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-blue-700 dark:text-blue-300 mb-1">
                      Next Step — Remediation Phase
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Your corrective action plan is ready. Start remediation and submit evidence for validation.
                    </p>
                  </div>
                )}
                {!inRemediation && (
                  <p className="text-xs text-muted-foreground">
                    {isSubmitted
                      ? (isAllowlisted
                        ? "You can keep iterating answers and use Preview Report for QA validation."
                        : "This assessment has been finalized and cannot be edited.")
                      : isTriage
                        ? "Your responses help identify key cyber risks, dependencies, and red flags that may impact investment decisions."
                        : "Your responses help evaluate the Organization's cybersecurity readiness across critical security domains."}
                  </p>
                )}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 w-full">
                  {inRemediation && (
                    <Button
                      variant="default"
                      onClick={() => navigate(`/action-plan/${assessment.id}`)}
                      className="w-full sm:w-auto"
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      View Corrective Action Plan
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  )}
                  {buttons.map((btn, i) => {
                    // When in remediation, demote everything (incl. Continue/View Submission) to outline.
                    const isPrimary = !inRemediation && i === 0;
                    return (
                      <Button
                        key={i}
                        onClick={btn.action}
                        variant={isPrimary ? "default" : "outline"}
                        className="w-full sm:w-auto"
                      >
                        {i === 1 && <FileText className="h-4 w-4 mr-1" />}
                        {btn.text}
                        {isPrimary && <ChevronRight className="h-4 w-4 ml-1" />}
                      </Button>
                    );
                  })}
                </div>
              </>
            );
          })()}
            {(() => {
              const cond =
                (assessment.questionnaire_id as string) === 'telecom_v2' &&
                actionPlanPublished;
              console.log('[RespondentDashboard] CAP button render', {
                assessment_id: assessment.id,
                questionnaire_id: assessment.questionnaire_id,
                status: assessment.status,
                actionPlanPublished,
                action_plan_published_at: assessment.action_plan_published_at,
                conditionTrue: cond,
              });
              return null;
            })()}
        </div>

        {/* QA Reset Button — only visible for allowlisted users */}
        {isAllowlisted && (
          <div className="mt-4 pt-4 border-t border-border">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowResetDialog(true)}
              className="text-destructive border-destructive/30 hover:bg-destructive/10"
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              Reset Assessment
            </Button>
            <p className="text-[10px] text-muted-foreground mt-1">QA only — clears all answers and resets status to Draft.</p>
          </div>
        )}
      </Card>

      {/* QA Reset Confirmation Dialog */}
      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Assessment?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to reset all answers? This cannot be undone. All responses, progress, and submission status will be cleared.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resetting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetAssessment}
              disabled={resetting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {resetting ? "Resetting…" : "Reset All Answers"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
